import React, { useEffect } from 'react'
import { Alert, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import apiClient from '../../api/client'
import EmptyScreen from '../../components/EmptyScreen'
import ExampleCardSkeleton from '../../components/skeleton/ExampleCardSkeleton'
import { useAppDispatch, useAppSelector } from '../../store'
import { loadFailed, loadStarted, loadSucceeded, selectExample } from '../../store/exampleSlice'

// Minimal runnable example route. Demonstrates the four states every data-backed page needs
// (loading / error / empty / loaded), wired through the one Redux slice and the shared axios
// client — the pattern a real page follows, without any product-specific logic.
const ExamplePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { status, error, items } = useAppSelector(selectExample)

  useEffect(() => {
    dispatch(loadStarted())
    apiClient
      .get<string[]>('/example/items')
      .then((response) => {
        // Only trust a JSON array. With no backend the same-origin server answers /example/items
        // with the SPA's own index.html (a 200 whose body is HTML, "You need to enable
        // JavaScript…"), and a misconfigured backend may return any shape — anything that is not an
        // array renders as empty instead of crashing on `.map` ("items.map is not a function").
        dispatch(loadSucceeded(Array.isArray(response.data) ? response.data : []))
      })
      .catch((err) => {
        // Distinguish "a configured backend responded with an error" from "there is no backend yet"
        // (this scaffold ships with none by default). Only the former is a real error state; with
        // no REACT_APP_SERVICE_URL the same-origin server returns a 404 or the SPA shell, which must
        // render empty rather than alarm a fresh checkout.
        if (process.env.REACT_APP_SERVICE_URL && err?.response) {
          dispatch(loadFailed(err.response.statusText || 'Request failed'))
        } else {
          dispatch(loadSucceeded([]))
        }
      })
  }, [dispatch])

  if (status === 'loading' || status === 'idle') {
    return (
      <Stack spacing={2} data-testid="example-loading">
        <ExampleCardSkeleton />
        <ExampleCardSkeleton />
      </Stack>
    )
  }

  if (status === 'failed') {
    return <Alert severity="error">{error ?? 'Something went wrong.'}</Alert>
  }

  if (items.length === 0) {
    return (
      <EmptyScreen
        containerHeight="240px"
        mainText="Nothing here yet"
        secondaryText="Items loaded from REACT_APP_SERVICE_URL will show up here."
      />
    )
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Example items</Typography>
      <List>
        {items.map((item) => (
          <ListItem key={item}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
    </Stack>
  )
}

export default ExamplePage
